import { useQuery } from '@tanstack/react-query'

import { PipelineJobSubmissionPromptTrigger } from '@sparkai/shared-types'

import { apiClient } from '../../apiClient'

type PipelineResponse = {
  pipelineId: string
  programId: string
  dateCreated: string
  version: number
  jobs: {
    pipelineJobId: string
    jobOrder: number
    name: string
    submissionDelay: number
    isSubmissionPromptEnabled: boolean
    submissionPromptTrigger: PipelineJobSubmissionPromptTrigger
    submissionPrompt: string
    noChangePrompt: string
    allowEscalation: boolean
    escalationText: string
    autoZoomOnPredictions: boolean
    enableImageShade: boolean
    hidePredictionLabel: boolean
    loadAllMedia: boolean
    expectedMediaMinimumCount: number
    expectedMediaMaximumCount: number
    taskLevelActions: boolean
    showPlaybackControls: boolean
    isRequiredToViewAllMedia: boolean
    isGIFAutoplayEnabled: boolean
    gifAutoplayCount: number | null
    steps?: Step[]
    quality?: {
      id: string
      jobId: string
      consensusMinMs: number
      consensusType: 'equal' | 'weighted'
      weightedConsensusThreshold: number | null
      qualityWeights: {
        id: string
        jobId: string
        featureId: string
        weight: number
      }[]
    }
    escalationOptions: {
      enabled: boolean
      options: {
        escalation: string
        considerResponse: boolean
      }[]
    }
    isOverlayApplied: boolean
    overlays?: {
      pipelineJobOverlayId: string
      pipelineJobId: string
      mediaId: string
      cameraName: string
      vehicleType: string
    }[]
  }[]
}

type Condition = {
  featureId: string
  condition: 'is' | 'is-not'
  value: string
}

type Step = {
  id: string
  jobId: string
  stepOrder: number
  instructions: string
  featureIds: string[]
  conditions: Condition[]
}

export const PIPELINE_QUERY_KEY = 'program-pipeline'

export function useGetPipeline(programId?: string) {
  return useQuery({
    queryKey: [PIPELINE_QUERY_KEY, programId],
    queryFn: async () =>
      (await apiClient.get<PipelineResponse>(`/int/v3/programs/${programId}/pipeline`)).data,
    enabled: Boolean(programId),
    refetchOnWindowFocus: false,
  })
}
